import { useId, useState } from 'react'
import { cn } from '@/lib/utils'
import { columnName, parseSpreadsheetPreview } from './spreadsheet'

/** Workbook sheets as tabs over a cell grid (demo `spreadsheet-preview.tsx`). */
export function SpreadsheetPreview({ text }: { text: string }) {
  const sheets = parseSpreadsheetPreview(text)
  const [active, setActive] = useState(0)
  const id = useId()
  const sheet = sheets[active] || sheets[0]
  if (!sheet) {
    return (
      <pre className="whitespace-pre-wrap break-words text-xs leading-5 [overflow-wrap:anywhere]">
        {text || 'No cells were found in this file.'}
      </pre>
    )
  }
  const columns = Math.max(1, ...sheet.rows.map((row) => row.length))
  const activeIndex = sheets.indexOf(sheet)
  return (
    <div className="min-w-0">
      <div aria-label="Workbook sheets" className="mb-2 flex gap-1 overflow-x-auto border-b" role="tablist">
        {sheets.map((item, index) => (
          <button
            aria-controls={`${id}-panel`}
            aria-selected={sheet === item}
            className={cn(
              'shrink-0 border-b-2 px-3 py-2 text-sm font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50',
              sheet === item ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:bg-muted/60',
            )}
            id={`${id}-tab-${index}`}
            key={index}
            onClick={() => setActive(index)}
            onKeyDown={(event) => {
              let next = index
              if (event.key === 'ArrowRight') next = (index + 1) % sheets.length
              else if (event.key === 'ArrowLeft') next = (index - 1 + sheets.length) % sheets.length
              else if (event.key === 'Home') next = 0
              else if (event.key === 'End') next = sheets.length - 1
              else return
              event.preventDefault()
              setActive(next)
              document.getElementById(`${id}-tab-${next}`)?.focus()
            }}
            role="tab"
            tabIndex={sheet === item ? 0 : -1}
            type="button"
          >
            {item.name || `Sheet ${index + 1}`}
          </button>
        ))}
      </div>
      <div
        aria-labelledby={`${id}-tab-${activeIndex}`}
        className="max-h-[62dvh] overflow-auto rounded-md border outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        id={`${id}-panel`}
        role="tabpanel"
        tabIndex={0}
      >
        <table className="w-full border-collapse text-left text-xs">
          <caption className="sr-only">{sheet.name} cell preview</caption>
          <thead className="sticky top-0 bg-muted">
            <tr>
              <th className="border-b border-r px-2 py-2 text-center font-medium text-muted-foreground" scope="col">
                <span className="sr-only">Row</span>
              </th>
              {Array.from({ length: columns }, (_, index) => (
                <th className="min-w-28 border-b border-r px-3 py-2 font-medium text-muted-foreground" key={index} scope="col">
                  {columnName(index)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sheet.rows.map((row, rowIndex) => (
              <tr className="hover:bg-muted/40" key={rowIndex}>
                <th className="border-b border-r bg-muted/60 px-2 py-2 text-right font-normal tabular-nums text-muted-foreground" scope="row">
                  {rowIndex + 1}
                </th>
                {Array.from({ length: columns }, (_, column) => (
                  <td className="max-w-80 whitespace-pre-wrap break-words border-b border-r px-3 py-2 align-top [overflow-wrap:anywhere]" key={column}>
                    {row[column] || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!sheet.rows.length ? <p className="p-4 text-sm text-muted-foreground">This sheet is empty.</p> : null}
      </div>
      {sheet.truncated ? (
        <p className="mt-2 text-sm text-muted-foreground">Preview shortened. Download the workbook to see all cells.</p>
      ) : null}
    </div>
  )
}
